import {Poll} from "@/types/Poll";
import {Card, CardContent} from "@/components/ui/card";
import {Button} from "@/components/ui/button";
import {
    Heart,
    X,
    LineChart,
    BarChart,
    Calendar,
    CalendarCheck,
    Clock,
    Tag,
    Users,
    Gamepad2,
    Trophy,
    Binary,
    Atom,
    Lightbulb,
    Monitor,
    Award,
    Check
} from "lucide-react";
import {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend} from "recharts";
import {categories} from "@/data/categories";
import {useIsMobile} from "@/hooks/use-mobile";
import {ChartContainer, ChartTooltipContent} from "@/components/ui/chart";
import {Separator} from "@/components/ui/separator";
import {Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@/components/ui/tooltip";
import {VoteConfirmationModal} from "@/components/VoteConfirmationModal";
import {useAuth} from "@/contexts/AuthContext.tsx";
import {useLoginModal} from "@/contexts/LoginModalContext.tsx";
import {toast} from "sonner";
import {ColorPairs} from "@/constants/colors.ts";
import chroma from 'chroma-js';

interface PollCardProps {
    poll: Poll;
}

export const ChartColors: string[] = ColorPairs.map((c) =>
    chroma(c.backgroundColor).darken(0.5).saturate(1.2).hex()
);
// Generate random voting data for the pie chart
const generatePieData = (poll: Poll) => {
    const options = poll.options || [];
    const colors = ['#155231', '#D4A574', '#3B82F6', '#F59E0B', '#EF4444'];
    const summary = poll?.summary || [];
    /*.map((detail) => {
    return {
        name: detail.optionTitle,
        value: parseFloat(
            detail.percentage?.toFixed(2) || "0",
            // (detail.vote_count / pollDetail?.poll?.totalVotes * 100).toFixed(2)
        ),
    };
}).filter(detail => detail.value > 0)*/
    return options?.map((option, index) => {
        return {
            name: option.title,
            value: summary.find((x) => x.optionId == option.id).percentage,
            color: ColorPairs[index % ColorPairs.length].backgroundColor
            // color: ChartColors[index % ChartColors.length]
        }
    })
    /*return summary ? summary?.map((sum, index) => {
        let percentage;
        /!*if (index === 0) {
            percentage = Math.floor(Math.random() * 40) + 30; // 30-70%
        } else if (index === 1) {
            percentage = Math.floor(Math.random() * 30) + 20; // 20-50%
        } else {
            percentage = Math.floor(Math.random() * 20) + 10; // 10-30%
        }*!/

        return {
            name: sum.optionTitle,
            value: sum.percentage,
            color: colors[index % colors.length]
        };
    }): null;*/
};

// Generate random voting data for the line chart
const generateVotingData = (days: number, optionsCount: number = 2) => {
    const data = [];
    const today = new Date();

    for (let i = 0; i < days; i++) {
        const date = new Date();
        date.setDate(today.getDate() - (days - i - 1));
        const formattedDate = `${date.getMonth() + 1}/${date.getDate()}`;

        let yesPercent = Math.floor(Math.random() * 40) + 20;
        let noPercent = Math.floor(Math.random() * 40) + 20;
        let thirdPercent = 0;

        if (optionsCount >= 3) {
            thirdPercent = Math.floor(Math.random() * 30) + 15;
            // Normalize to 100%
            const total = yesPercent + noPercent + thirdPercent;
            yesPercent = Math.round((yesPercent / total) * 100);
            noPercent = Math.round((noPercent / total) * 100);
            thirdPercent = 100 - yesPercent - noPercent;
        } else {
            // For 2 options, normalize to 100%
            const total = yesPercent + noPercent;
            yesPercent = Math.round((yesPercent / total) * 100);
            noPercent = 100 - yesPercent;
        }

        if (i > days / 2) {
            yesPercent = Math.min(yesPercent + i * 2, 92); // Trending upward toward end
        }

        const dailyVotes = Math.floor(Math.random() * 500) + 100;

        const dataPoint: any = {
            date: formattedDate,
            yes: yesPercent,
            no: noPercent,
            votes: dailyVotes,
        };

        if (optionsCount >= 3) {
            dataPoint.third = thirdPercent;
        }

        data.push(dataPoint);
    }

    return data;
};

const ParticipationBar = ({totalVotes}: { totalVotes: number }) => {
    // Determine which section the votes fall into
    const getActiveSection = (votes: number) => {
        if (votes <= 1000) return 0;
        if (votes <= 10000) return 1;
        if (votes <= 25000) return 2;
        if (votes <= 50000) return 3;
        if (votes <= 100000) return 4;
        if (votes <= 250000) return 5;
        if (votes <= 500000) return 6;
        return 7;
    };

    const activeSection = getActiveSection(totalVotes);

    // Sections ordered from top to bottom (high to low participation)
    // Colors: darkest at top, lightest at bottom
    const sections = [
        {label: "500.001+", reward: "25000bp", color: "bg-green-800", activeColor: "bg-green-900", index: 7},
        {label: "250.001-500K", reward: "22000bp", color: "bg-green-700", activeColor: "bg-green-800", index: 6},
        {label: "100.001-250K", reward: "18000bp", color: "bg-green-600", activeColor: "bg-green-700", index: 5},
        {label: "50.001-100K", reward: "12000bp", color: "bg-green-500", activeColor: "bg-green-600", index: 4},
        {label: "25.001-50K", reward: "10000bp", color: "bg-green-400", activeColor: "bg-green-500", index: 3},
        {label: "10.001-25K", reward: "7500bp", color: "bg-green-300", activeColor: "bg-green-400", index: 2},
        {label: "1.001-10K", reward: "4000bp", color: "bg-green-200", activeColor: "bg-green-300", index: 1},
        {label: "0-1K", reward: "2000bp", color: "bg-green-100", activeColor: "bg-green-200", index: 0}
    ];

    return (
        <TooltipProvider>
            <div className="flex flex-row h-4 w-full ml-0 lg:flex-col lg:h-full lg:w-4 lg:ml-3">
                {sections.map((section, displayIndex) => (
                    <div key={section.index} className="flex-1 flex flex-row lg:flex-col">
                        <UITooltip>
                            <TooltipTrigger asChild>
                                <div
                                    className={`flex-1 ${section.index <= activeSection ? section.activeColor : section.color} 
                             ${displayIndex === 0 ? 'rounded-l-md lg:rounded-t-md' : ''} 
                             ${displayIndex === sections.length - 1 ? 'rounded-r-md lg:rounded-b-md' : ''} 
                             cursor-pointer hover:opacity-80 transition-opacity`}
                                />
                            </TooltipTrigger>
                            <TooltipContent side="left" sideOffset={5}
                                            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg lg:side-left">
                                <div className="text-sm font-medium">
                                    <div className="text-gray-900 dark:text-white">{section.label} votes</div>
                                    <div
                                        className="text-green-600 dark:text-green-400 font-semibold">{section.reward}</div>
                                </div>
                            </TooltipContent>
                        </UITooltip>
                        {displayIndex < sections.length - 1 && (
                            <div className="w-px bg-gray-300 mx-0.5 lg:w-auto lg:h-px lg:mx-0 lg:my-0.5"/>
                        )}
                    </div>
                ))}
            </div>
        </TooltipProvider>
    );
};

const PollCard: React.FC<PollCardProps> = ({poll}) => {
    const [voteStatus, setVoteStatus] = useState<'none' | 'yes' | 'no' | 'third'>('none');
    const [showVoteModal, setShowVoteModal] = useState(false);
    const [pendingVote, setPendingVote] = useState<{
        type: 'yes' | 'no' | 'third',
        option: string,
        id: string
    } | null>(null);

    const navigate = useNavigate();
    const isMobile = useIsMobile();


    const pieData = generatePieData(poll);
    const latestData = pieData[0] || {value: 45};
    const yesPercentage = pieData[0]?.value || 45;
    const noPercentage = pieData[1]?.value || 35;
    const thirdPercentage = pieData[2]?.value || 20;
    const {user, session, loading} = useAuth();
    const {openLoginModal} = useLoginModal();
    const handleTitleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        navigate(`/polls/${poll.slug}`, {replace: false});
    };
    const [userVote, setUserVote] = useState(null);

    useEffect(() => {
        if (poll?.userVotes) {
            setUserVote(poll.options.find(p => p.id == poll.userVotes?.find(u => u.poll_id == poll.id)?.option_id));
        }
        console.log("userVote:", "poll changed:", poll);

    }, [poll])

    // const userVote = poll.options.find(p => p.id == poll.userVotes?.find(u => u.poll_id == poll.id)?.option_id);

    const handleVoteClick = (vote: 'yes' | 'no' | 'third', optionTitle: string, e: React.MouseEvent, id: string) => {
        e.stopPropagation();
        if (voteStatus !== 'none') return;

        if (!loading && (!user || !session)) {
            // console.log("hello");
            openLoginModal();
            return;
        }
        setPendingVote({type: vote, option: optionTitle, id});

        setShowVoteModal(true);
    };

    const handleSubmit = async (id: string) => {
        if (!session) {
            return toast.error('Please login to cast your vote');
        }

        try {
            const url = `${import.meta.env.VITE_API_URL}/polls/vote`;
            const headers = {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`
            };
            const body = JSON.stringify({pollId: poll.id, optionId: id});

            const response = await fetch(url, {
                method: 'POST',
                headers,
                body,
            });

            if (!response.ok) {
                return toast.error('An error occurred while saving vote!');
            }

            // await queryClient.invalidateQueries({queryKey: ['userVote', pollDetail]});
            // await queryClient.invalidateQueries({queryKey: ['polls', slug]});
            // await refetch();
            const responseResult = await response.json();
            if (responseResult?.data) {
                console.log("test");
                poll.summary = responseResult.data?.summary;
                poll.userVotes = responseResult.data?.userVotes;
                setUserVote(poll.options.find(p => p.id == poll.userVotes?.find(u => u.poll_id == poll.id)?.option_id));
            }
            console.log("responseResult:", responseResult);
            setShowVoteModal(false);
            setPendingVote(null);
            return toast.success('Your vote has been saved!');
        } catch (error) {
            // console.error('Error:', error);
        }

    }

    const confirmVote = async () => {
        if (pendingVote) {
            setVoteStatus(pendingVote.type);
            await handleSubmit(pendingVote.id);
            // setShowVoteModal(false);
            // setPendingVote(null);

        }
    };

    const cancelVote = () => {
        setShowVoteModal(false);
        setPendingVote(null);
    };

    // Get category icon - updated to show the correct icon based on category
    const getCategoryIcon = () => {
        const categorySlug = poll.categories?.slug;

        // Return appropriate icon based on category slug
        let IconComponent = Tag;

        switch (categorySlug) {
            case 'technology':
                IconComponent = Monitor;
                break;
            case 'sports':
                IconComponent = Trophy;
                break;
            case 'fun':
                IconComponent = Lightbulb;
                break;
            case 'politics':
                IconComponent = Binary;
                break;
            case 'science':
                IconComponent = Atom;
                break;
            case 'gaming':
                IconComponent = Gamepad2;
                break;
            default:
                IconComponent = Tag;
        }

        return (
            <div className="bg-green-50 dark:bg-green-900/30 p-4 rounded-lg">
                <IconComponent size={36} className="text-green-700 dark:text-green-300"/>
            </div>
        );
    };

    // Check if the poll has exactly two options and they are "Yes" and "No"
    const isYesNoPool = poll.options && poll.options.length === 2 &&
        poll.options.map(opt => opt.title.toLowerCase().trim()).includes('yes') &&
        poll.options.map(opt => opt.title.toLowerCase().trim()).includes('no');

    const CustomTooltip = ({active, payload}: any) => {
        if (active && payload && payload.length) {
            return (
                <div
                    className="bg-white dark:bg-gray-800 p-3 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
                    <p className="font-medium text-gray-900 dark:text-white">{payload[0].name}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-300">{formatNumber(payload[0].value || 0)}%</p>
                </div>
            );
        }
        return null;
    };
    const formatNumber = (n: number) => (+n.toFixed(2)).toString();
    return (
        <>
            <Card
                className="w-full bg-white dark:bg-gray-800 overflow-hidden border border-dashed border-gray-300 dark:border-gray-600">
                <CardContent className="p-6">
                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 flex-col-reverse lg:flex-row">
                        {/* Right side - Pie Chart with Participation Bar (on desktop), Second on mobile */}
                        <div className="col-span-1 lg:col-span-3 order-last lg:order-last">
                            {/* Mobilde chart ve bar alt alta, chart ortalanmış */}
                            <div
                                className="flex flex-col lg:flex-row items-center lg:items-stretch justify-center h-full w-full">
                                <div
                                    className="w-full flex justify-center items-center lg:justify-center lg:items-center">
                                    <ChartContainer
                                        className="w-full max-w-[260px] sm:max-w-[320px] h-64"
                                        config={{
                                            yes: {
                                                label: "Yes",
                                                theme: {
                                                    light: "#155231",
                                                    dark: "#4ADE80"
                                                }
                                            },
                                            no: {
                                                label: "No",
                                                theme: {
                                                    light: "#D4A574",
                                                    dark: "#FB7185"
                                                }
                                            },
                                            third: {
                                                label: "Third",
                                                theme: {
                                                    light: "#3B82F6",
                                                    dark: "#60A5FA"
                                                }
                                            }
                                        }}
                                    >
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie
                                                    // style={{filter: 'brightness(3) contrast(0.7)'}}
                                                    data={pieData}
                                                    cx="50%"
                                                    cy="50%"
                                                    innerRadius={40}
                                                    outerRadius={80}
                                                    paddingAngle={2}
                                                    dataKey="value"
                                                    stroke="#fff"
                                                    strokeWidth={0.5}
                                                    fillOpacity={1}
                                                    opacity={1}
                                                    fill="#fff"
                                                >
                                                    {pieData.map((entry, index) => (
                                                        <Cell key={`cell-${index}`}
                                                              fill={entry.color}
                                                              fillOpacity={1}
                                                              opacity={1}
                                                              strokeOpacity={1}
                                                              // style={{filter: 'brightness(3) contrast(0.7)'}}
                                                        />
                                                    ))}
                                                </Pie>
                                                <Tooltip content={<CustomTooltip/>}/>
                                                <Legend
                                                    // style={{filter: 'brightness(0.85) contrast(1.2)'}}
                                                    opacity={1}
                                                    fillOpacity={1}
                                                    verticalAlign="bottom"
                                                    height={36}
                                                    formatter={(value, entry: any) => (
                                                        <span style={{
                                                            color: entry.color,
                                                            // filter: 'brightness(0.85) contrast(1.2)'
                                                        }}>
                              {value} ({formatNumber(entry.payload.value || 0)}%)
                            </span>
                                                    )}
                                                />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </ChartContainer>
                                </div>
                                {/* Mobilde participation bar chart'ın altında, lg ve üstünde yanında */}
                                {/*<div className="hidden lg:flex lg:w-auto lg:ml-3 lg:items-center">
                  <ParticipationBar totalVotes={poll.totalVotes || 0} />
                </div>*/}
                            </div>
                        </div>

                        {/* Left side - Poll info (on desktop), First on mobile */}
                        <div className="col-span-1 lg:col-span-2 order-first lg:order-first flex flex-col">
                            <div className="flex items-start gap-4 mb-4">
                                {/* Hide category icon on mobile, show on desktop */}
                                {!isMobile && getCategoryIcon()}
                                <div>
                                    <h3
                                        className="font-bold text-3xl text-gray-900 dark:text-white cursor-pointer hover:text-black transition-colors"
                                        onClick={handleTitleClick}
                                    >
                                        {poll.title}
                                    </h3>
                                </div>
                            </div>

                            {isYesNoPool ? (
                                <div className="flex gap-2 w-full mt-6">
                                    {userVote?.title == 'Yes' ? (
                                        <Button
                                            className="w-full bg-[#E5F4EA] hover:bg-[#D5E8DC] text-[#155231] font-medium min-w-[120px]"
                                        >
                                            <Check className="w-4 h-4 min-w-[16px] min-h-[16px] mr-1"/> Voted
                                        </Button>
                                    ) : (
                                        <Button
                                            className="w-full bg-[#E5F4EA] hover:bg-[#D5E8DC] text-[#155231] font-medium min-w-[120px]"
                                            onClick={(e) => handleVoteClick('yes', 'Yes', e, poll.options.find(o => o.title == 'Yes').id)}
                                            disabled={voteStatus === 'no'}
                                        >
                                            <Heart
                                                className="w-4 h-4 min-w-[16px] min-h-[16px] mr-1"/> Yes {formatNumber(yesPercentage || 0)}%
                                        </Button>
                                    )}

                                    {userVote?.title == 'No' ? (
                                        <Button
                                            className="w-full bg-[#FFF7DE] hover:bg-[#FFE9B0] text-[#6F3B01] font-medium min-w-[120px]"
                                        >
                                            <Check className="w-4 h-4 min-w-[16px] min-h-[16px] mr-1"/> Voted
                                        </Button>
                                    ) : (
                                        <Button
                                            className="w-full bg-[#FFF7DE] hover:bg-[#FFE9B0] text-[#6F3B01] font-medium min-w-[120px]"
                                            onClick={(e) => handleVoteClick('no', 'No', e, poll.options.find(o => o.title == 'No').id)}
                                            disabled={voteStatus === 'yes'}
                                        >
                                            <X className="w-4 h-4 min-w-[16px] min-h-[16px] mr-1"/> No {formatNumber(noPercentage || 0)}%
                                        </Button>
                                    )}
                                </div>
                            ) : (
                                <div className="space-y-1 mt-6 flex-grow">
                                    {poll.options?.map((option, index) => {
                                        const isSelected = (index === 0 && voteStatus === 'yes') ||
                                            (index === 1 && voteStatus === 'no') ||
                                            (index === 2 && voteStatus === 'third');
                                        const isDisabled = !!userVote && !isSelected;
                                        const isLastOption = index === poll.options!.length - 1;
                                        const colors = ['#155231', '#D4A574', '#3B82F6', '#F59E0B', '#EF4444'];
                                        const hoverColors = ['#0e3e25', '#b6845e', '#2563eb', '#d97706', '#dc2626'];


                                        // const buttonClass = `bg-[${pieData.find((x)=> x.name == option.title).color}] hover:bg-[${pieData.find((x)=> x.name == option.title).color}] text-black dark:text-black dark:hover:text-[#0e3e25]`;
                                        // const buttonClass = {backgroundColor: pieData.find((x)=> x.name == option.title).color, color: 'white'}
                                        const colorPair = ColorPairs[index % ColorPairs.length];
                                        // const colorPair = {backgroundColor: ChartColors[index % ChartColors.length], color: ColorPairs[index % ColorPairs.length].color};
                                        // const buttonClass = `bg-[${colors[index % colors.length]}] hover:bg-[${pieData.find((x)=> x.name == option.title).color}] text-[${colors[index % colors.length]}]`;
                                        let percentage = 0;
                                        let voteType: 'yes' | 'no' | 'third' = 'yes';

                                        if (index === 0) {
                                            // buttonClass = "bg-[#E5F4EA] hover:bg-[#D5E8DC] text-[#155231]";
                                            percentage = yesPercentage;
                                            voteType = 'yes';
                                        } else if (index === 1) {
                                            // buttonClass = "bg-[#FFF7DE] hover:bg-[#FFE9B0] text-[#6F3B01]";
                                            percentage = noPercentage;
                                            voteType = 'no';
                                        } else if (index === 2) {
                                            // buttonClass = "bg-[#EBF4FF] hover:bg-[#DBEAFE] text-[#1E40AF]";
                                            percentage = thirdPercentage;
                                            voteType = 'third';
                                        }

                                        return (
                                            <div key={option.id}>
                                                <div className="flex items-center gap-2 py-2">
                                                    <div className="flex-1 text-sm font-medium">{option.title}</div>
                                                    <Button
                                                        // className={`${buttonClass} whitespace-nowrap w-[100px]`}
                                                        className={`whitespace-nowrap w-[100px]`}
                                                        style={colorPair}
                                                        // style={{...colorPair, filter: 'brightness(3) contrast(0.7)'}}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (!userVote || userVote.id != option.id) {
                                                                handleVoteClick(voteType, option.title, e, option.id);
                                                            }
                                                        }}
                                                        disabled={!!userVote && userVote?.id != option.id}
                                                    >
                                                        {/*{isSelected ? (*/}
                                                        {userVote?.id == option.id ? (
                                                            <>
                                                                <Check className="w-3.5 h-3.5 mr-1"/> Voted
                                                            </>
                                                        ) : (
                                                            <>
                                                                Vote {formatNumber(poll?.summary?.find((x) => x.optionId == option.id)?.percentage || 0)}%
                                                            </>
                                                        )}
                                                    </Button>
                                                </div>
                                                {!isLastOption && (
                                                    <Separator className="border-dashed opacity-30 my-1"/>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            <div
                                className="grid grid-cols-3 gap-4 mt-auto pt-6 text-xs text-gray-500 dark:text-gray-400">
                                <div className="flex items-center">
                                    <Clock className="w-3.5 h-3.5 mr-1"/>
                                    <span>{new Date(poll.createdAt).toLocaleDateString()}</span>
                                </div>
                                <div className="flex items-center justify-center">
                                    <Award className="w-3.5 h-3.5 mr-1"/>
                                    <span className="font-medium">2000bp</span>
                                </div>
                                <div className="flex items-center justify-end">
                                    <CalendarCheck className="w-3.5 h-3.5 mr-1"/>
                                    <span>{new Date(poll.endDate).toLocaleDateString()}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            <VoteConfirmationModal
                open={showVoteModal}
                onConfirm={confirmVote}
                onCancel={cancelVote}
                pollTitle={poll.title}
                selectedOption={pendingVote?.option || ''}
                optionType={pendingVote?.type || 'yes'}
            />
        </>
    );
};

export default PollCard;
